import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { Grid } from "@mui/material";
import { FormikSaveBar } from "../../../utils/FormikSaveBar";
import { PMField } from "../../../utils/PMField";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PMSwitch from "../../../utils/PMSwitch";
import {
  createHourRateAsync,
  updateHourRateAsync,
  getHourRateAsync,
} from "../../../../slices/offer-calculation-hour-rate";
import { mapErrors } from "../../../Utils";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { useSettings } from "../../../../contexts/settings-context";

export const OfferCalculationHourRateInfo = (props) => {
  const { t } = useTranslation();
  const { hourRate, isLoading } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { priceFormatter } = useSettings();

  const updateOrCreateHourRate = async (values) => {
    if (values.id) {
      return dispatch(updateHourRateAsync({ id: values.id, payload: values }));
    } else {
      return dispatch(createHourRateAsync(values));
    }
  };

  if (isLoading && hourRate?.id) {
    return <ResourceLoading />;
  }

  return (
    <>
      <Formik
        initialValues={{
          id: hourRate?.id,
          title: hourRate?.title || "",
          description: hourRate?.description || "",
          hour_rate: hourRate?.hour_rate || 0,
          cost_rate: hourRate?.cost_rate || 0,
          active: hourRate?.active !== undefined ? hourRate?.active : true,
          row_order: hourRate?.row_order || 0,
        }}
        enableReinitialize={true}
        validationSchema={Yup.object({
          title: Yup.string().required(t("Tittel er påkrevd")),
          hour_rate: Yup.number().required(t("Timepris er påkrevd")).min(0, t("Timepris må være positiv")),
        })}
        onSubmit={async (values, helpers) => {
          try {
            helpers.setSubmitting(true);
            const result = await updateOrCreateHourRate(values);
            
            // Check if the action was rejected
            if (result.type?.endsWith('/rejected')) {
              const errors = result.payload?.response?.data?.errors || result.payload?.errors;
              const errorMessage = mapErrors(errors) || result.payload?.message || t("Noe gikk galt. Kunne ikke lagre data!");
              toast.error(errorMessage);
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: errorMessage });
              helpers.setSubmitting(false);
              return;
            }
            
            // Check if the action was fulfilled and has valid data
            if (result.type?.endsWith('/fulfilled')) {
              const payload = result.payload;
              
              // Check if payload indicates an error
              if (payload?.error || payload?.errors) {
                const errorMessage = mapErrors(payload.errors) || payload.message || t("Noe gikk galt. Kunne ikke lagre data!");
                toast.error(errorMessage);
                helpers.setStatus({ success: false });
                helpers.setErrors({ submit: errorMessage });
                helpers.setSubmitting(false);
                return;
              }
              
              // Success
              toast.success(t("Data ble lagret!"));
              helpers.setStatus({ success: true });
              helpers.setSubmitting(false);
              
              // If this was a new hour rate, navigate to edit page
              if (!values.id && payload?.id) {
                navigate(`/dashboard/offers/hour-rates/${payload.id}`, { replace: true });
                dispatch(getHourRateAsync(payload.id));
              }
              return;
            }
            
            // Fallback: if result doesn't have expected structure
            if (result.payload) {
              const payload = result.payload;
              if (payload.error || payload.errors || payload.response?.status) {
                const errorMessage = mapErrors(payload.errors) || payload.message || t("Noe gikk galt. Kunne ikke lagre data!");
                toast.error(errorMessage);
                helpers.setStatus({ success: false });
                helpers.setErrors({ submit: errorMessage });
                helpers.setSubmitting(false);
                return;
              }
              
              // Success
              toast.success(t("Data ble lagret!"));
              helpers.setStatus({ success: true });
              helpers.setSubmitting(false);
              
              // If this was a new hour rate, navigate to edit page
              if (!values.id && payload.id) {
                navigate(`/dashboard/offers/hour-rates/${payload.id}`, { replace: true });
                dispatch(getHourRateAsync(payload.id));
              }
              return;
            }
            
            // If we get here, something unexpected happened
            console.error("Unexpected result structure:", result);
            toast.error(t("Noe gikk galt. Kunne ikke lagre data!"));
            helpers.setStatus({ success: false });
            helpers.setSubmitting(false);
          } catch (err) {
            console.error("Error saving hour rate:", err);
            toast.error(t("Noe gikk galt. Kunne ikke lagre data!"));
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: err.message });
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form>
            <Grid item xs={12}>
              <div className="shadow sm:overflow-auto sm:rounded-md">
                <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      {t("Timesats informasjon")}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500"></p>
                  </div>

                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6 sm:col-span-6">
                      <PMField
                        name="title"
                        type="text"
                        label={t("Tittel")}
                        required
                        {...formik}
                      />
                    </div>

                    <div className="col-span-6">
                      <PMField
                        name="description"
                        type="text"
                        label={t("Beskrivelse")}
                        multiline
                        rows={3}
                        {...formik}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <PMField
                        name="hour_rate"
                        type="number"
                        label={t("Timepris")}
                        required
                        {...formik}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <PMField
                        name="cost_rate"
                        type="number"
                        label={t("Kostpris")}
                        {...formik}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <PMField
                        name="row_order"
                        type="number"
                        label={t("Rekkefølge")}
                        {...formik}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <PMSwitch label={t("Aktiv")} name="active" {...formik} />
                    </div>
                  </div>
                </div>
              </div>
            </Grid>

            <FormikSaveBar {...formik} />
          </Form>
        )}
      </Formik>
    </>
  );
};

